import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { Send, Login } from "@mui/icons-material";

export default function CommentSection({ toolId, user, login, isLoggedIn }) {
  const isAdmin = user?.isAdmin;
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null); // comment._id or null
  const [replyText, setReplyText] = useState("");

  const COMMENTS_PER_PAGE = 20;

  useEffect(() => {
    fetchComments(1);
  }, [toolId]);

  const renderComment = (comment, level = 0) => {
    const usernameInitial = comment.username?.charAt(0)?.toUpperCase() || "?";
    const displayName = comment.username || "Pending Approval";
    const isReplying = replyingTo === comment._id;

    return (
      <Box
        key={comment._id}
        sx={{ display: "flex", gap: 2, ml: level * 4, mt: 2 }}
      >
        <Avatar
          src={comment.profile_pic}
          alt={comment.username}
          sx={{ mt: "4px" }}
        >
          {usernameInitial}
        </Avatar>

        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle2" fontWeight="medium">
              {displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              • {timeAgo(comment.createdAt)}
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ mt: 0.5, whiteSpace: "pre-line" }}>
            {comment.content}
          </Typography>

          {comment.username && (
            <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
              {["👍", "👎", "❤️"].map((emoji) => {
                const count = comment.reactions?.[emoji]?.length || 0;
                const reacted =
                  user && comment.reactions?.[emoji]?.includes(user.name);

                return (
                  <IconButton
                    key={emoji}
                    onClick={() => handleReaction(comment._id, emoji)}
                    size="small"
                    sx={{
                      fontSize: "0.875rem",
                      padding: "4px 6px",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: reacted ? "primary.main" : "grey.300",
                      backgroundColor: reacted
                        ? "primary.light"
                        : "transparent",
                      color: reacted ? "primary.main" : "text.secondary",
                      minWidth: 0,
                    }}
                  >
                    {emoji}
                    {count > 0 && (
                      <Typography
                        variant="caption"
                        sx={{ ml: 0.5, fontSize: "0.75rem" }}
                      >
                        {count}
                      </Typography>
                    )}
                  </IconButton>
                );
              })}
            </Box>
          )}

          {isAdmin && (
            <Button
              size="small"
              onClick={() => {
                if (replyingTo === comment._id) {
                  setReplyingTo(null);
                  setReplyText("");
                } else {
                  setReplyingTo(comment._id);
                  setReplyText("");
                }
              }}
            >
              {isReplying ? "Cancel Reply" : "Reply"}
            </Button>
          )}

          {isReplying && (
            <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                size="small"
                placeholder="Write a reply..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
              />
              <Button
                variant="contained"
                size="small"
                onClick={() => handlePostReply(comment._id)}
                disabled={!replyText.trim()}
              >
                Send
              </Button>
            </Box>
          )}

          {/* Render Replies */}
          {comment.replies &&
            comment.replies.map((reply) => renderComment(reply, level + 1))}
        </Box>
      </Box>
    );
  };

  const handlePostReply = async (parentId) => {
    const trimmedReply = replyText.trim();
    if (!trimmedReply) return;

    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/comments`,
        {
          toolId,
          username: user.name,
          content: trimmedReply,
          parentId, // important: link reply to parent comment
        }
      );

      // Add the new reply as a nested comment under the parent
      setComments((prevComments) =>
        prevComments.map((comment) =>
          comment._id === parentId
            ? {
                ...comment,
                replies: comment.replies
                  ? [res.data.comment, ...comment.replies]
                  : [res.data.comment],
              }
            : comment
        )
      );

      setReplyingTo(null);
      setReplyText("");
      setSuccess(
        res.data.message ||
          "Reply submitted successfully and is pending approval."
      );
    } catch (err) {
      console.error("Failed to post reply", err);
      setError(err?.response?.data?.message || "Failed to post reply");
    }
    setLoading(false);
  };

  const fetchComments = async (pageNumber) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/comments?toolId=${toolId}&page=${pageNumber}&limit=${COMMENTS_PER_PAGE}`
      );

      const newComments = res.data;

      if (pageNumber === 1) {
        setComments(newComments);
      } else {
        setComments((prev) => [...prev, ...newComments]);
      }

      setHasMore(newComments.length === COMMENTS_PER_PAGE);
      setPage(pageNumber);
    } catch (err) {
      console.error("Failed to fetch comments", err);
      setError(
        err.response?.data?.message || err.message || "Failed to load comments"
      );
    }
  };

  const handleReaction = async (commentId, emoji) => {
    if (!user) return;

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/comments/${commentId}/reaction`,
        {
          reactionType: emoji,
          username: user.name,
        }
      );

      setComments((prev) =>
        prev.map((comment) =>
          comment._id === commentId
            ? { ...comment, reactions: res.data.reactions }
            : comment
        )
      );
    } catch (err) {
      console.error("Failed to update reactions", err);
    }
  };

  const handlePostComment = async () => {
    if (!isLoggedIn) {
      setError("Please sign in to post a comment");
      setTimeout(() => login(), 2000);
      return;
    }

    const trimmedComment = newComment.trim();
    const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/i;

    if (trimmedComment.length < 4) {
      setError("Comment must be at least 4 characters");
      return;
    }
    if (trimmedComment.length > 500) {
      setError("Comment must be less than or equal to 500 characters");
      return;
    }
    
    if (urlRegex.test(trimmedComment)) {
      setError("Links and URLs are not allowed in comments");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/comments`,
        {
          toolId,
          username: user.name,
          content: trimmedComment,
          parentId: null,
        }
      );
      setComments((prev) => [res.data.comment, ...prev]);
      setNewComment("");
      setSuccess(
        res.data.message ||
          "Comment submitted successfully and is pending approval."
      );
    } catch (err) {
      console.error("Failed to post comment", err);
      setError(err?.response?.data?.message || "Failed to post comment");
    }
    setLoading(false);
  };

  const handleShowMore = async () => {
    setLoadingMore(true);
    await fetchComments(page + 1);
    setLoadingMore(false);
  };

  const timeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    let interval = Math.floor(seconds / 31536000);
    if (interval >= 1) return `${interval} year${interval > 1 ? "s" : ""} ago`;
    interval = Math.floor(seconds / 2592000);
    if (interval >= 1) return `${interval} month${interval > 1 ? "s" : ""} ago`;
    interval = Math.floor(seconds / 86400);
    if (interval >= 1) return `${interval} day${interval > 1 ? "s" : ""} ago`;
    interval = Math.floor(seconds / 3600);
    if (interval >= 1) return `${interval} hour${interval > 1 ? "s" : ""} ago`;
    interval = Math.floor(seconds / 60);
    if (interval >= 1)
      return `${interval} minute${interval > 1 ? "s" : ""} ago`;
    return "just now";
  };

  const handleCloseSnackbar = () => {
    setError(null);
    setSuccess(null);
  };

  return (
    <Card sx={{ maxWidth: 1200, width: "100%", mx: "auto", my: 4 }}>
      <CardContent>
        <Typography variant="h5" component="h2" gutterBottom>
          Join the Conversation
        </Typography>

        {/* Comment Input */}
        <Box sx={{ display: "flex", gap: 2, mb: 4 }}>
          <Avatar
            src={user?.profile_pic}
            alt={user?.name}
            sx={{ bgcolor: "primary.main" }}
          >
            {!user?.profile_pic && user?.name?.charAt(0).toUpperCase()}
          </Avatar>

          <Box sx={{ flexGrow: 1 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              placeholder={
                !isLoggedIn
                  ? "Sign in to leave a comment"
                  : "Share your thoughts… Got feedback, found a bug, or have ideas to improve this tool?"
              }
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                endIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Send />
                  )
                }
                onClick={handlePostComment}
                disabled={loading || !newComment.trim()}
              >
                {loading ? "Posting..." : "Post Comment"}
              </Button>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Comments List */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {comments.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              py={4}
            >
              No comments yet. Be the first to share your thoughts!
            </Typography>
          ) : (
            <>
              {comments.map((comment) => renderComment(comment))}

              {hasMore && (
                <Box sx={{ textAlign: "center", mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={handleShowMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? "Loading..." : "Show more comments"}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </CardContent>

      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          {success}
        </Alert>
      </Snackbar>
    </Card>
  );
}
